'use client';

import { InView } from 'react-intersection-observer';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { Section, Spacing, NoticeWithIcon, LinkByEnvironment, LogImpression, LogClick } from '~/components';
import { Typography, Loader } from '@bgzt/ui';

import { Brand } from '~/apis/mdms';
import { brandsQueryOptions, searchProductsInfiniteQueryOptions } from '~/queries';
import { ProductDataV2 } from '~/apis/search';

import { BUTTON_ID, CONTENT_TYPE, EVENT_ACTION, EVENT_TYPE, PAGE_ID, VIEW_ID } from '~/apis/log/constant';
import { goToFullWebviewOnApp } from '~/services/app-interface';
import { useAppDidFavorite, useEnvironmentInfo, useSendLog, useUpdateInfiniteQueryProductFavorite } from '~/hooks';
import { ProductListTemp } from '~/components/ProductListTemp';
import { ProductItem } from '~/components/ProductItem';

import styles from './RecentlyUploaded.module.css';

export const RecentlyUploaded = () => {
  const { isWebview } = useEnvironmentInfo();
  const sendLog = useSendLog();
  const queryOptions = searchProductsInfiniteQueryOptions(
    {
      page: 0,
      size: 20,
      ref: 'latest',
      filters: false,
    },
    { enabled: true },
  );
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery(queryOptions);
  const { data: brandsData } = useQuery(brandsQueryOptions({ allowDisable: false }));
  const { updateInfiniteQueryProductFavorite } = useUpdateInfiniteQueryProductFavorite();

  useAppDidFavorite((pid, type) => {
    updateInfiniteQueryProductFavorite({
      queryKey: queryOptions.queryKey,
      pid: Number(pid),
      isFavorite: type === 'add',
    });
  });

  const getBrandById = (brandId?: number, brands?: Brand[]) => {
    if (!brandId || !brands) return;
    return brands?.find((brand) => brand.id === brandId);
  };

  const getBrandCategoryPath = (product: ProductDataV2, brands?: Brand[]) => {
    const brand = getBrandById(product.brandId, brands);
    return brand
      ? `/search/result?q=${brand?.nameKor}&categoryId=${product?.category?.id}`
      : `/category/${product?.category?.id}`;
  };

  const handleBrandCategoryShortCutClick = (product: ProductDataV2, brands?: Brand[]) => {
    const path = getBrandCategoryPath(product, brands);
    const url = isWebview ? process.env.NEXT_PUBLIC_DOMAIN + path : path;
    isWebview ? goToFullWebviewOnApp({ url }).go() : globalThis.window.open(url, '_blank');
  };

  const onInViewFetch = async (inView: boolean) => {
    if (!inView || !hasNextPage) return;

    if (!isFetchingNextPage) {
      await fetchNextPage();
      return;
    }
  };

  return (
    <Section className={styles.productListContainer}>
      <Typography variant="S1" color="gray-900" as="p">
        최근 올라온 메루카리 상품
      </Typography>
      <Spacing height={16} />
      {!isLoading && data?.pages?.length === 0 && (
        <>
          <Spacing height={80} />
          <NoticeWithIcon iconType="product">
            <Typography variant="S1" color="gray-900">
              아직 등록된 상품이 없어요.
            </Typography>
          </NoticeWithIcon>
        </>
      )}

      <ProductListTemp col={2}>
        {data?.pages?.map((item, index) => {
          const product = item as ProductDataV2;
          const path = `/products/${product.pid}`;
          const query = `?ref_page=${PAGE_ID.HOME}&ref_position=${index}`;
          const brand = getBrandById(product.brandId, brandsData);

          const logParams = {
            page_id: PAGE_ID.HOME,
            view_id: VIEW_ID.PRODUCT,
            content_id: String(product.pid),
            content_type: CONTENT_TYPE.MERCARI,
            content_position: index,
            url: goToFullWebviewOnApp({
              url: `${globalThis?.window?.location.origin}/products/${product.pid}`,
            }).to,
            tracking: product?.tracking,
          };

          return (
            <li key={product.pid}>
              <LogImpression
                params={{
                  ...logParams,
                  event_type: EVENT_TYPE.IMPRESSION,
                  event_action: EVENT_ACTION.IMPRESSION,
                }}
              >
                <LogClick
                  params={{
                    ...logParams,
                    event_type: EVENT_TYPE.SELECT_PRODUCT,
                    event_action: EVENT_ACTION.SELECT_PRODUCT,
                  }}
                >
                  <LinkByEnvironment href={path + query} target={'_blank'}>
                    <ProductItem
                      pid={product.pid}
                      image={product.productImage}
                      name={product.name}
                      location={product.location}
                      updatedBefore={product.updatedBefore}
                      price={product.price}
                      status={product.status}
                      faved={product.faved}
                      category={product.category}
                      brand={brand}
                      discountRate={product.discount?.rate}
                      onFavoriteClick={(value) => {
                        sendLog({
                          ...logParams,
                          event_type: EVENT_TYPE.WISHLIST,
                          event_action: value ? EVENT_ACTION.ADD_TO_WISHLIST : EVENT_ACTION.REMOVE_FROM_WISHLIST,
                        });
                      }}
                      onBrandCategoryShortCutClick={() => {
                        sendLog({
                          ...logParams,
                          page_label: product.pid.toString(),
                          button_label: product.category?.id,
                          ...(product.brandId && { button_label2: product.brandId }),
                          event_action: EVENT_ACTION.SELECT_BUTTON,
                          event_type: EVENT_TYPE.SELECT,
                          button_id: BUTTON_ID.SEARCH_SHORT_CUT,
                          url: goToFullWebviewOnApp({
                            url: `${globalThis?.window?.location.origin}${getBrandCategoryPath(product, brandsData)}`,
                          }).to,
                        });
                        handleBrandCategoryShortCutClick(product, brandsData);
                      }}
                    />
                  </LinkByEnvironment>
                </LogClick>
              </LogImpression>
            </li>
          );
        })}
      </ProductListTemp>

      <InView as="div" onChange={onInViewFetch}>
        <Spacing height={30} />
        {isFetchingNextPage && (
          <div className={styles.loader} data-testid="in-view-element">
            <Loader size={24} />
          </div>
        )}
      </InView>
    </Section>
  );
};
